
import { Vue, Options, prop } from "vue-class-component";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import Checkbox from "primevue/checkbox";
import { getToken } from "@/app/infrastructures/misc/Cookies";
import parsingLinkGcpToAws from "@/app/infrastructures/misc/common-library/ParsingLinkGcpToAws";
import needsAuth from "@/app/infrastructures/misc/common-library/NeedsAuth";
import { reactive } from "vue";

class Props {
  modelValue = prop<boolean>({ required: true });
  data = prop<Array<any>>({ default: [], type: Array });
  maxHeight = prop<string>({ default: "", type: String });
  isCustom = prop<boolean>({ default: false, type: Boolean });
  image = prop<string>({ default: "", type: String });
}

@Options({
  name: "PreviewImageAuth",
  emits: ["update:modelValue", "close"],
  components: { Checkbox }
})
export default class extends Vue.with(Props) {
  // Declare the types for TypeScript
  objectUrls!: Record<number, string>;
  loading!: Record<number, boolean>;
  abortControllers!: Record<number, AbortController>;

  // 1x1 transparent PNG placeholder
  placeholderImage =
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVQIW2P8z/CfBwAFgwJ/lb1tJwAAAABJRU5ErkJggg==";

  constructor(...args: any[]) {
    super(...args);
    // ✅ make these reactive at runtime
    this.objectUrls = reactive({});
    this.loading = reactive({});
    this.abortControllers = reactive({});
  }

  created() {
    // Preload current index image when opened
    for (const index in this.computedData) {
      this.prefetchAtIndex(parseInt(index));
    }
  }

  beforeUnmount() {
    this.revokeAll();
    this.abortAll();
  }

  get initialIndex(): number {
    const idx = this.image
      ? this.computedData.findIndex((x: any) => x.image === this.image)
      : 0;
    return idx < 0 ? 0 : idx;
  }

  get maxHeightDefault() {
    return (
      (5 / 6) * (MainAppController.screenSize?.height || window.innerHeight)
    );
  }

  get computedData() {
    const temp: any[] = this.data || [];
    if (this.isCustom) {
      temp.unshift("");
      return temp;
    }
    return temp
      .map((item: any) => {
        const url: string = item?.url || item;
        const image: string = item?.image || item;
        return { image, url };
      })
      .filter((item: any) => this.getType(item) !== "pdf");
  }

  get currentIndex(): number {
    return this.initialIndex;
  }

  get currentObjectUrl(): string | null {
    return this.objectUrls[this.initialIndex] || null;
  }

  get currentItem(): any {
    return this.computedData[this.initialIndex];
  }

  getType(item: any): string {
    const url: string = item?.url || "";
    const parts = url.split("?")[0].split(".");
    const ext = parts[parts.length - 1] || "";
    if (needsAuth(url)) return url.slice(-3);
    return ext.toLowerCase();
  }

  // Decide which src to display for a given slide index/item
  displaySrc(index: number, item: any): string {
    const existing = this.objectUrls[index];
    if (existing) return existing;
    const url = item?.url || "";
    // For public urls, fall back to direct url so image shows immediately
    if (url && !needsAuth(url)) return url;
    // For protected urls we wait for blob to be fetched
    return this.placeholderImage;
  }

  onClose() {
    this.$emit("update:modelValue", false);
    this.$emit("close");
    this.abortAll();
  }

  async handleOpenInNewTab(url?: string, forceType?: "pdf" | "img") {
    if (!url) return;
    try {
      MainAppController.showLoading();
      const blob = await this.fetchBlobWithAuth(url);
      const objUrl = window.URL.createObjectURL(blob);
      window.open(objUrl, "_blank");
      // Do not revoke immediately, allow the new tab to load. Revoke later.
      setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
    } catch (e) {
      // TODO: connect with global error/toast if available
      // eslint-disable-next-line no-console
      console.error("Failed to open file:", e);
    } finally {
      MainAppController.closeLoading();
    }
  }

  async prefetchAtIndex(index: number) {
    const item = this.computedData[index];
    if (!item) return;
    if (this.getType(item) === "pdf") return;

    // already loaded
    if (this.objectUrls[index] || this.loading[index]) return;

    // Public image: use direct URL
    if (!needsAuth(item.url)) {
      this.objectUrls = { ...this.objectUrls, [index]: item.url };
      return;
    }

    this.loading[index] = true;
    try {
      const blob = await this.fetchBlobWithAuth(item.url);
      const mime =
        blob.type && blob.type !== "application/octet-stream"
          ? blob.type
          : "image/jpeg";
      const objUrl = URL.createObjectURL(new Blob([blob], { type: mime }));

      // ✅ Replace entire object to ensure Vue reactivity
      this.objectUrls = { ...this.objectUrls, [index]: objUrl };
      this.loading[index] = false;
    } catch (e) {
      console.error("Failed to prefetch image:", e);
    } finally {
      this.loading[index] = false;
    }
  }

  revokeAll() {
    Object.values(this.objectUrls).forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        /* noop */
      }
    });
    this.objectUrls = {};
  }

  abortAll() {
    Object.values(this.abortControllers).forEach((c) => c.abort());
    this.abortControllers = {} as any;
  }

  async fetchBlobWithAuth(url: string): Promise<Blob> {
    const parsedUrl = parsingLinkGcpToAws(url);

    const controller = new AbortController();
    const signal = controller.signal;

    const init: RequestInit = { signal };
    if (needsAuth(url)) {
      const headers = new Headers();
      headers.append("authorization", `Bearer ${getToken()}`);
      init.headers = headers;
    }

    const res = await fetch(parsedUrl, init);
    if (!res.ok) {
      let data: any = null;
      try {
        data = await res.json();
      } catch (e) {
        /* ignore */
      }
      const error: any = new Error("Failed to fetch file");
      (error.response as any) = { status: res.status, data };
      throw error;
    }
    return await res.blob();
  }
}
